import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../helpers/axiosConfig';

// Async thunks for API calls
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ email, password, fcm_token = '' }, { rejectWithValue }) => {
    try {
      const response = await api.post('/login', {
        email,
        password,
        fcm_token,
      });

      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'An error occurred during login'
      );
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (
    { name, email, phone, password, password_confirmation },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/register', {
        name,
        email,
        phone,
        password,
        password_confirmation,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'An error occurred during registration'
      );
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ email, otp, fcm_token = '' }, { rejectWithValue }) => {
    try {
      const response = await api.post('/verifyotp', {
        email,
        otp,
        fcm_token,
      });

      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          'An error occurred during OTP verification'
      );
    }
  }
);

// Resend OTP
export const resendOtp = createAsyncThunk(
  'auth/resendOtp',
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await api.post('/register', { email });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to resend verification code'
      );
    }
  }
);

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return rejectWithValue('No token found');
    }

    try {
      return { token };
    } catch (error) {
      localStorage.removeItem('auth_token');
      return rejectWithValue('Invalid token');
    }
  }
);

// Add logout API thunk
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/logout');
      localStorage.removeItem('auth_token');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'An error occurred during logout'
      );
    }
  }
);

const initialState = {
  user: null,
  token: localStorage.getItem('auth_token') || null,
  isAuthenticated: !!localStorage.getItem('auth_token'),
  pendingVerification: false,
  verificationEmail: null,
  loading: false,
  error: null,
  successMessage: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    cancelVerification: (state) => {
      state.pendingVerification = false;
      state.verificationEmail = null;
      state.error = null;
      state.successMessage = null;
    },
    loginSuccess: (state, action) => {
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.error = null;
      state.successMessage = 'Successfully logged in';
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.token) {
          state.token = action.payload.token;
          state.isAuthenticated = true;
          state.successMessage = action.payload.message;
        } else {
          // For cases where login is successful but user is not verified
          state.pendingVerification = true;
          state.verificationEmail = action.meta.arg.email;
          state.successMessage = action.payload.message;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;

        // Handle case where user is not verified
        if (
          action.payload ===
          'Your account is not verified. Please verify your email.'
        ) {
          state.pendingVerification = true;
          state.verificationEmail = action.meta.arg.email;
        }
      })

      // Register cases
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.pendingVerification = true;
        state.verificationEmail = action.meta.arg.email;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // OTP verification cases
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingVerification = false;
        state.verificationEmail = null;

        if (action.payload.token) {
          state.token = action.payload.token;
          state.isAuthenticated = true;
        }

        state.successMessage = action.payload.message;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Check auth cases
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.isAuthenticated = true;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.token = null;
        state.isAuthenticated = false;
      })

      // Resend OTP cases
      .addCase(resendOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          'Verification code has been resent to your email.';
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add logout cases
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.pendingVerification = false;
        state.verificationEmail = null;
        state.successMessage =
          action.payload.message || 'Successfully logged out';
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Still clear the local state even if the API call fails
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.pendingVerification = false;
        state.verificationEmail = null;
      });
  },
});

export const { clearErrors, cancelVerification, loginSuccess } =
  authSlice.actions;
export default authSlice.reducer;
